import {
  getAvailableValidationsGrpc,
  getDocumentInfoGrpc,
  getLastReportGrpc,
  getValidationSettingsGrpc,
} from '../../../shared/api/grpc/validationGrpcClient';
import { webLog } from '../../../shared/api/grpc/webLogger';
import { useSnackbarStore } from '../../../shared/stores/useSnackbarStore';
import {
  compareValidationDefinitionsByCriticality,
  isSelectableValidationId,
  normalizeValidationId,
} from '../validationConstants';
import type { PcfValidationReport, ValidationDefinition } from '../types';
import { MOCK_VALIDATIONS } from './validationMockData';
import { applyValidationMetadata } from './validationStateHelpers';
import {
  filterSelectableValidationIds,
  getSelectableValidationDefinitionIds,
  parseValidationReportJson,
  type ValidationReportEnvelope,
} from './validationReportNormalizer';
import { normalizeValidationRuntimeSettings, type ValidationRuntimeSettings } from './validationRuntimeSettings';
import { USE_GRPC, USE_MOCK } from './validationRuntimeEnv';

export function getSelectableMockValidations(): ValidationDefinition[] {
  return MOCK_VALIDATIONS.filter((validation) => isSelectableValidationId(validation.Id));
}

export interface LoadValidationsDependencies {
  call: (method: string, params?: Record<string, unknown>) => Promise<any>;
  setValidations: (validations: ValidationDefinition[]) => void;
  setSelectedValidations: (ids: number[]) => void;
}

export async function loadValidations({
  call,
  setValidations,
  setSelectedValidations,
}: LoadValidationsDependencies): Promise<void> {
  const selectableMockValidations = getSelectableMockValidations();

  if (USE_GRPC) {
    try {
      const grpcResult = await getAvailableValidationsGrpc();
      if (grpcResult.success && grpcResult.validations) {
        const mapped: ValidationDefinition[] = grpcResult.validations
          .map((v) => ({
            Id: normalizeValidationId(v.id),
            Title: v.title,
            Description: v.description,
            DefaultSeverity: v.defaultSeverity as any,
            Category: v.category,
            IsEnabled: v.isEnabled,
            CriticalityRank: v.criticalityRank,
          }))
          .filter((validation) => isSelectableValidationId(validation.Id))
          .sort(compareValidationDefinitionsByCriticality);
        setValidations(mapped);
        setSelectedValidations(
          filterSelectableValidationIds(
            mapped.filter((v) => v.IsEnabled).map((v) => v.Id),
            mapped
          )
        );
      } else {
        setValidations(selectableMockValidations);
        setSelectedValidations(getSelectableValidationDefinitionIds(selectableMockValidations));
      }
    } catch {
      setValidations(selectableMockValidations);
      setSelectedValidations(getSelectableValidationDefinitionIds(selectableMockValidations));
    }
    return;
  }

  try {
    const result = await call('validation.getAvailableValidations');
    if (result.success && result.validations) {
      const mappedValidations = (result.validations as ValidationDefinition[])
        .map((validation) => ({
          ...validation,
          Id: normalizeValidationId(validation.Id),
        }))
        .filter((validation) => isSelectableValidationId(validation.Id))
        .sort(compareValidationDefinitionsByCriticality);

      setValidations(mappedValidations);
      setSelectedValidations(getSelectableValidationDefinitionIds(mappedValidations));
      webLog.info(
        'validation.page-load',
        `validations(host): loaded count=${mappedValidations.length}`
      );
    } else {
      webLog.warn(
        'validation.page-load',
        `validations(host): empty success=${result?.success} error='${result?.error ?? ''}'`
      );
      useSnackbarStore.getState().showError(result.error || 'Failed to load validations');
    }
  } catch (err: any) {
    webLog.warn(
      'validation.page-load',
      `validations(host): threw error=${err instanceof Error ? err.message : String(err)}`
    );
    useSnackbarStore.getState().showError(err.Message || 'Failed to load validations');
  }
}

export interface LoadDocumentInfoDependencies {
  call: (method: string, params?: Record<string, unknown>) => Promise<any>;
  setDocumentInfo: (info: any) => void;
  onComplete: () => void;
}

export async function loadDocumentInfo({
  call,
  setDocumentInfo,
  onComplete,
}: LoadDocumentInfoDependencies): Promise<void> {
  if (USE_GRPC) {
    try {
      const grpcResult = await getDocumentInfoGrpc();
      if (grpcResult.success && grpcResult.documentName) {
        setDocumentInfo({
          DocumentName: grpcResult.documentName,
          DocumentPath: grpcResult.documentPath || '',
        });
      } else {
        setDocumentInfo(null);
      }
      webLog.info(
        'validation.page-load',
        `document-info(grpc): success=${grpcResult.success} hasName=${Boolean(grpcResult.documentName)} name='${grpcResult.documentName ?? ''}'`
      );
    } catch (err: any) {
      setDocumentInfo(null);
      webLog.warn(
        'validation.page-load',
        `document-info(grpc): threw error=${err instanceof Error ? err.message : String(err)}`
      );
    }
    onComplete();
    return;
  }

  try {
    const result = await call('validation.getDocumentInfo');
    const success = result?.success === true || result?.isSuccess === true || result?.IsSuccess === true;
    const documentName =
      result?.info?.DocumentName ?? result?.documentName ?? result?.DocumentName ?? '';
    const documentPath =
      result?.info?.DocumentPath ?? result?.documentPath ?? result?.DocumentPath ?? '';
    if (success && documentName) {
      setDocumentInfo({ DocumentName: documentName, DocumentPath: documentPath });
    } else {
      setDocumentInfo(null);
    }
    webLog.info(
      'validation.page-load',
      `document-info(host): success=${success} hasName=${Boolean(documentName)} name='${documentName}' path='${documentPath}'`
    );
  } catch (err: any) {
    console.error('Failed to load document info:', err);
    setDocumentInfo(null);
    webLog.warn(
      'validation.page-load',
      `document-info(host): threw error=${err instanceof Error ? err.message : String(err)}`
    );
  }
  onComplete();
}

export interface LoadValidationSettingsDependencies {
  call: (method: string, params?: Record<string, unknown>) => Promise<any>;
  report: PcfValidationReport | null;
  setValidationSettings: (updater: (current: ValidationRuntimeSettings) => ValidationRuntimeSettings) => void;
}

export async function loadValidationSettings({
  call,
  report,
  setValidationSettings,
}: LoadValidationSettingsDependencies): Promise<void> {
  if (!USE_GRPC) {
    setValidationSettings((current) => normalizeValidationRuntimeSettings(report, current));
    return;
  }

  try {
    const grpcResult = await getValidationSettingsGrpc();
    if (grpcResult.success) {
      setValidationSettings((current) => normalizeValidationRuntimeSettings(grpcResult, current));
      return;
    }

    webLog.warn(
      'validation.page-load',
      `validation-settings(grpc): failed error='${grpcResult.error ?? ''}'`
    );
  } catch (err: any) {
    webLog.warn(
      'validation.page-load',
      `validation-settings(grpc): threw error=${err instanceof Error ? err.message : String(err)}`
    );
  }
}

export interface LoadLastReportDependencies {
  call: (method: string, params?: Record<string, unknown>) => Promise<any>;
  /**
   * A host-cached report was produced by some earlier setup this loader knows
   * nothing about, so the fingerprint slot is typed `null`: the loader is
   * structurally unable to claim the report describes the current context.
   */
  commitReport: (report: PcfValidationReport, source: string, contextFingerprint: null) => void;
  setValidationSettings: (updater: (current: ValidationRuntimeSettings) => ValidationRuntimeSettings) => void;
  setDocumentInfo: (updater: (current: any) => any) => void;
}

export async function loadLastReport({
  call,
  commitReport,
  setValidationSettings,
  setDocumentInfo,
}: LoadLastReportDependencies): Promise<void> {
  if (USE_MOCK) {
    return;
  }

  try {
    if (USE_GRPC) {
      const grpcResult = await getLastReportGrpc();
      if (!grpcResult.success || !grpcResult.hasReport || !grpcResult.reportJson) {
        return;
      }

      const parsedReport = parseValidationReportJson(grpcResult.reportJson, 'last-report-grpc');
      const cachedReport = applyValidationMetadata(parsedReport, {
        lastValidationDate: grpcResult.lastValidationDate || parsedReport.ValidationDate,
      });
      commitReport(cachedReport, 'last-report-grpc', null);
      setValidationSettings((current) => normalizeValidationRuntimeSettings(cachedReport, current));
      if (cachedReport.DocumentName) {
        setDocumentInfo((current: any) => current ?? { DocumentName: cachedReport.DocumentName });
      }
      return;
    }

    const result = await call('validation.getLastReport');
    const success = result?.success === true || result?.IsSuccess === true || result?.isSuccess === true;
    const hasReport = result?.hasReport === true || result?.HasReport === true;
    const reportJson = result?.reportJson || result?.ReportJson;
    if (!success || !hasReport || !reportJson) {
      webLog.info(
        'validation.page-load',
        `last-report(host): none (success=${success} hasReport=${hasReport}) - empty report state is expected for a model with no saved validation`
      );
      return;
    }

    const parsedReport = parseValidationReportJson(reportJson, 'last-report-host');
    const cachedReport = applyValidationMetadata(parsedReport, {
      lastValidationDate: result.lastValidationDate || result.LastValidationDate || parsedReport.ValidationDate,
      lastValidationCapturedAtUtc: result.lastValidationCapturedAtUtc || result.LastValidationCapturedAtUtc,
      lastValidationCapturedAtLocal: result.lastValidationCapturedAtLocal || result.LastValidationCapturedAtLocal,
    } as ValidationReportEnvelope);
    commitReport(cachedReport, 'last-report-host', null);
    setValidationSettings((current) => normalizeValidationRuntimeSettings(cachedReport, current));
    if (cachedReport.DocumentName) {
      setDocumentInfo((current: any) => current ?? { DocumentName: cachedReport.DocumentName });
    }
  } catch (error) {
    webLog.warn(
      'validation-preflight',
      `last-report-load: skipped error=${error instanceof Error ? error.message : String(error)}`
    );
  }
}
